<template>
  <div class="http-checker-app" data-testid="http-checker-app">
    <div class="http-checker-container">
      <h2 v-if="config.show_title" class="app-title" data-testid="http-checker-title">{{ config.title }}</h2>
      
      <a-card class="checker-card" data-testid="http-checker-card">
        <url-input
          v-model="urls"
          :loading="isChecking"
          :max-urls="config.max_urls"
          @submit="handleCheck"
          @clear="handleClear"
        />
      </a-card>
      
      <a-alert
        v-if="error"
        type="error"
        :message="error"
        closable
        @close="error = null"
        style="margin-top: 16px;"
        data-testid="http-checker-error"
      />
      
      <div ref="resultsSection">
        <results-table
          v-if="results.length > 0"
          :results="results"
          :loading="isChecking"
          :show-export="config.show_export"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from 'vue'
import { message } from 'ant-design-vue'
import UrlInput from './components/UrlInput.vue'
import ResultsTable from './components/ResultsTable.vue'
import { useHttpChecker } from './composables/useHttpChecker'
import type { HttpCheckerConfig } from './types'

interface Props {
  mountElementId: string
}

const props = defineProps<Props>()

const urls = ref('')
const config = ref<HttpCheckerConfig>({
  instance_id: '',
  title: 'HTTP Status Checker',
  show_title: true,
  max_urls: 50,
  show_export: true
})

const { isChecking, results, error, checkUrls, clear } = useHttpChecker()

const resultsSection = ref<HTMLElement | null>(null)

watch(results, (newResults) => {
  if (newResults.length > 0) {
    nextTick(() => {
      resultsSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }
})

onMounted(() => {
  // Get configuration from data attribute
  const mountElement = document.getElementById(props.mountElementId)
  if (mountElement) {
    const configAttr = mountElement.getAttribute('data-config')
    if (configAttr) {
      try {
        const parsedConfig = JSON.parse(configAttr)
        config.value = { ...config.value, ...parsedConfig }
      } catch (err) {
        console.error('Failed to parse HTTP checker config:', err)
      }
    }
  }
})

const handleCheck = async (urlList: string[]) => {
  if (urlList.length === 0) {
    message.warning('Please enter at least one URL')
    return
  }
  
  if (urlList.length > config.value.max_urls) {
    message.error(`Maximum ${config.value.max_urls} URLs allowed`)
    return
  }
  
  await checkUrls(urlList)
}

const handleClear = () => {
  clear()
}
</script>

<style scoped lang="scss">
.http-checker-app {
  padding: 20px 0;
  
  .http-checker-container {
    max-width: 1200px;
    margin: 0 auto;
    
    .app-title {
      text-align: center;
      margin-bottom: 24px;
      font-size: 28px;
      font-weight: 600;
    }
    
    .checker-card {
      margin-bottom: 24px;
    }
  }
}
</style>
